import { Box3, SpotLight, Vector3 } from "three";
import NameLabelComponent from "../../components/text-components/NameLabelComponent";
import { IMaterialSet } from "../../services/MaterialManager";
import BimProduct from "../BimProduct";
import NameLabelGeometry from "../geometry/NameLabelGeometry";
import MaterialPair from "../material/MaterialPair";
import ShapeTypeEnum from "../representation2d/ShapeTypeEnum";
import ShapeRepresentation2d from "../representation2d/ShapeRepresentation2d";
import { calculatePolygonCentroid } from "../../utils/mathUtils";
import { sceneUnitsFromMm } from "../../utils/scaler";
import { RenderOrderEnum } from "../../enums/RenderOrderEnum";
import LayerMappingsEnum from "../../enums/LayerMappingsEnum";
import IProduct from "../../interfaces/IProduct";
import { StageTypeEnum } from "../../store/viewerState/ViewerMode/ViewerMode";
import { useUserStateStore } from "../../store/userStateStore";
import { getRoomForSpace, useRoomNotesStore } from "../../store/roomNotesStore";

const USE_ROOM_LIGHTS = false;

export default class BimSpace extends BimProduct {

    nameLabel: NameLabelGeometry;
    spotLight: SpotLight | undefined;
    #unsubscribeUserState: () => void;
    #unsubscribeRoomNotes: () => void;

    constructor(data: IProduct, parent: BimProduct | undefined) {
        super(data, parent);

        this.nameLabel = new NameLabelGeometry( this.capitalizeFirstLetter(this.data.name) || "Unnamed Space",
                                                new Vector3(),
                                                RenderOrderEnum.RoomNames,
                                                LayerMappingsEnum.RoomNameLabels,
                                                {
                                                    alwaysOnTop: true,
                                                    borderRadius: sceneUnitsFromMm(100),
                                                    backgroundColor: "white",
                                                    textColor: "darkslategray",
                                                    fontSize: 0.15,
                                                    note: this.getRoom()?.note ?? null,
                                                    canAddNote: this.canAddNote(),
                                                    globalId: this.data.globalId
                                                });

        // Notes and the right to edit them load from CoreAPI alongside the model, and notes change when saved
        this.#unsubscribeUserState = useUserStateStore.subscribe((state, prev) => {
            if (state.canEditRoomNotes !== prev.canEditRoomNotes) this.updateNameLabelNote();
        });
        this.#unsubscribeRoomNotes = useRoomNotesStore.subscribe((state, prev) => {
            if (getRoomForSpace(state.rooms, this.data.globalId) !== getRoomForSpace(prev.rooms, this.data.globalId)) this.updateNameLabelNote();
        });
        
        if (USE_ROOM_LIGHTS)
        {
            this.spotLight = new SpotLight(
                0xffffff,
                2,
                5,
                5
            );

            this.group.add(this.spotLight!);
        }
    }

    // The CoreAPI room matching this space. A space without one has no note and can't get one.
    private getRoom() {
        return getRoomForSpace(useRoomNotesStore.getState().rooms, this.data.globalId);
    }

    private canAddNote(): boolean {
        return useUserStateStore.getState().canEditRoomNotes && this.getRoom() !== undefined;
    }

    private updateNameLabelNote(): void {
        this.nameLabel.setNote(this.getRoom()?.note ?? null, this.canAddNote());
    }

    dispose() {
        super.dispose();
        this.#unsubscribeUserState();
        this.#unsubscribeRoomNotes();
        this.nameLabel.dispose();
    }

    private capitalizeFirstLetter(str: string): string {

        // If string is not all uppercase, do not change it
        const isAllUpperCase = str === str.toUpperCase();

        // Handle empty string, 
        // handle only all upper case strings (for example Living Room is already better than Living room)  
        // do not change short strings (MH, WC, KPH, etc.)
        if (!str || !isAllUpperCase || str.length < 4) 
            return str; 

        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    updateRepresentations( materialPairs?: Record<number, MaterialPair>, 
                            materialSet?: IMaterialSet,
                            stageType?: StageTypeEnum) {
        super.updateRepresentations(materialPairs, materialSet, stageType);

        const boundingBox = new Box3().setFromObject(this.group);
        
        const center = boundingBox.getCenter(new Vector3());
        
        
             /* const rectLight = new RectAreaLight(
            0xffffff,
            1,
            boundingBox.getSize(new Vector3()).x,
            boundingBox.getSize(new Vector3()).y
        ); */

        if (this.spotLight)
        {
            this.spotLight.position.copy(center);
            this.spotLight.target.position.copy(center);
            this.spotLight.target.position.z -= sceneUnitsFromMm(1000);
            this.spotLight.rotation.x = -Math.PI / 2;  // face straight down
            this.spotLight.castShadow = true;
        }
        

        // Add space name label
        const shapeRepresentation = this.data.representations2d.find(r => r.shapeType === ShapeTypeEnum.Shape);
        if (shapeRepresentation instanceof ShapeRepresentation2d)
        {
            const centroid = calculatePolygonCentroid(shapeRepresentation.outerProfile); 
            this.nameLabel.setPosition(new Vector3(centroid.x, centroid.y, sceneUnitsFromMm(10)));  
                                                     
            this.representationGroup.add(this.nameLabel.getGroup());
        }
    };

    onRotationOffsetChanged(rotationOffset: number) {
        super.onRotationOffsetChanged(rotationOffset);
        this.nameLabel.setRotationOffset(rotationOffset);
    }

};